export const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"] as const;
export type ClickIdKey = (typeof CLICK_ID_KEYS)[number];
export const CLICK_ID_TTL_DAYS = 90;

type ClickIds = Record<ClickIdKey, string | null>;

const expiresKey = (key: ClickIdKey) => `${key}_expires`;

const getCookieDomain = (): string | undefined => {
  const { hostname } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") return undefined;

  // app.alidrop.co / www.alidrop.co → .alidrop.co (cross-subdomain hop)
  const parts = hostname.split(".");
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join(".")}`;
  }
  return undefined;
};

const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const domain = getCookieDomain();
  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  if (domain) cookie += `; domain=${domain}`;
  if (window.location.protocol === "https:") cookie += "; Secure";
  document.cookie = cookie;
};

const getCookie = (name: string): string | null => {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${encodeURIComponent(name)}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
};

const persistClickId = (
  key: ClickIdKey,
  value: string,
  { overwrite = false } = {}
) => {
  // First-touch: keep the original click ID for the TTL window unless overwrite requested
  if (!overwrite) {
    const existing = getPersistedClickId(key);
    if (existing) return;
  }

  const expiresAt = Date.now() + CLICK_ID_TTL_DAYS * 864e5;
  localStorage.setItem(key, value);
  localStorage.setItem(expiresKey(key), String(expiresAt));
  setCookie(key, value, CLICK_ID_TTL_DAYS);
};

export const getPersistedClickId = (key: ClickIdKey): string | null => {
  const expiresRaw = localStorage.getItem(expiresKey(key));
  if (expiresRaw) {
    const expiresAt = Number(expiresRaw);
    if (Number.isFinite(expiresAt) && Date.now() > expiresAt) {
      localStorage.removeItem(key);
      localStorage.removeItem(expiresKey(key));
    } else {
      const fromLs = localStorage.getItem(key);
      if (fromLs) return fromLs;
    }
  } else {
    const fromLs = localStorage.getItem(key);
    if (fromLs) return fromLs;
  }

  return getCookie(key);
};

export const getAllPersistedClickIds = (): ClickIds => ({
  gclid: getPersistedClickId("gclid"),
  gbraid: getPersistedClickId("gbraid"),
  wbraid: getPersistedClickId("wbraid"),
});

export const captureClickIdsFromSearch = (
  search: string | URLSearchParams = window.location.search
): Partial<ClickIds> => {
  const params =
    typeof search === "string"
      ? new URLSearchParams(search.startsWith("?") ? search : `?${search}`)
      : search;

  const captured: Partial<ClickIds> = {};
  for (const key of CLICK_ID_KEYS) {
    const value = params.get(key);
    if (value) {
      persistClickId(key, value);
      captured[key] = getPersistedClickId(key) || value;
    }
  }
  return captured;
};

/** Append persisted click IDs to a URL when missing (marketing → app domain hop). */
export const appendClickIdsToUrl = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.origin);
    const clickIds = getAllPersistedClickIds();
    for (const key of CLICK_ID_KEYS) {
      const value = clickIds[key];
      if (value && !parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, value);
      }
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

/**
 * Bing-parity aliases → Alidrop stored keys.
 * Google ValueTrack final URLs may use either naming style.
 */
export const UTM_ALIAS_MAP: Record<string, string> = {
  utm_campaign_name: "utm_campaign",
  utm_campaign_id: "utm_campaignid",
  utm_adgroup_name: "utm_adgroup",
  utm_adgroup_id: "utm_adgroup",
  campaignid: "utm_campaignid",
  adgroupid: "utm_adgroup",
  matchtype: "utm_matchtype",
  keyword: "utm_keyword",
};

export const resolveUtmParam = (
  searchParams: URLSearchParams,
  primaryKey: string,
  ...aliasKeys: string[]
): string | null => {
  const primary = searchParams.get(primaryKey);
  if (primary) return primary;
  for (const alias of aliasKeys) {
    const value = searchParams.get(alias);
    if (value) return value;
  }
  return null;
};

export const hasClickIdAttribution = (ids: Partial<ClickIds>): boolean =>
  !!(ids.gclid || ids.gbraid || ids.wbraid);
